import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { loadingTrue, loadingFalse } from "../../Reducer/loaderSlice";
import Handler from "../../Helpers/Handler";
import Select from "react-select";
import AdminHeader from "./Header";
import { Users, Search, Calendar, ChevronLeft, ChevronRight, Filter } from "lucide-react";

const statusOptions = [
    { value: "", label: "All Status" },
    { value: "ACTIVE", label: "ACTIVE" },
    { value: "INACTIVE", label: "INACTIVE" }
];

const AdminBusinessList = () => {
    const dispatch = useDispatch();

    const [list, setList] = useState([]);
    const [pagination, setPagination] = useState({ page: 1, total_pages: 1 });
    const [search, setSearch] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [status, setStatus] = useState(statusOptions[0]);
    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");
    const [page, setPage] = useState(1);

    const limit = 20;

    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearch(search), 500);
        return () => clearTimeout(timer);
    }, [search]);

    const fetchBusinesses = async () => {
        dispatch(loadingTrue());
        try {
            const res = await Handler({
                method: "get",
                url: `/admin/list.php?page=${page}&limit=${limit}&search=${debouncedSearch}&status=${status?.value}&from_date=${fromDate}&to_date=${toDate}`
            });

            if (res?.success) {
                setList(res?.data?.list || []);
                setPagination(res?.data?.pagination || {});
            }
        } catch (err) {
            Handler(err);
        }
        dispatch(loadingFalse());
    };

    useEffect(() => { setPage(1); }, [debouncedSearch, status, fromDate, toDate]);
    useEffect(() => { fetchBusinesses(); }, [page, debouncedSearch, status, fromDate, toDate]);

    // Custom styles for React Select to match navy theme
    const selectStyles = {
        control: (base) => ({
            ...base,
            border: '1px solid #dee2e6',
            borderRadius: '8px',
            padding: '2px',
            boxShadow: 'none',
            '&:hover': { borderColor: '#1e3a8a' }
        })
    };

    return (
        <div className="bg-soft-blue min-vh-100">
            <AdminHeader />
            <div className="container py-4 pb-5">

                {/* PAGE TITLE */}
                <div className="d-flex align-items-center mb-4">
                    <div className="icon-box-blue me-3 shadow-sm">
                        <Users size={22} />
                    </div>
                    <h3 className="fw-bold text-navy mb-0">Business Profiles</h3>
                </div>

                {/* FILTERS */}
                <div className="card border-0 shadow-sm mb-4 rounded-4">
                    <div className="card-header bg-white border-bottom-0 pt-3">
                        <div className="d-flex align-items-center text-primary fw-bold small text-uppercase">
                            <Filter size={14} className="me-2" /> Filter Profiles
                        </div>
                    </div>
                    <div className="card-body row g-3 pt-1">
                        <div className="col-md-3">
                            <label className="small fw-bold text-muted mb-1">Search</label>
                            <div className="input-group input-group-sm custom-input-group">
                                <span className="input-group-text bg-light border-end-0 text-muted"><Search size={14} /></span>
                                <input
                                    className="form-control bg-light border-start-0"
                                    placeholder="Name or ID..."
                                    value={search}
                                    onChange={e => setSearch(e.target.value)}
                                />
                            </div>
                        </div>

                        <div className="col-md-3">
                            <label className="small fw-bold text-muted mb-1">Status</label>
                            <Select
                                value={status}
                                onChange={setStatus}
                                options={statusOptions}
                                isSearchable={false}
                                styles={selectStyles}
                                className="react-select-container"
                                classNamePrefix="react-select"
                            />
                        </div>

                        <div className="col-md-3">
                            <label className="small fw-bold text-muted mb-1">From Date</label>
                            <div className="input-group input-group-sm custom-input-group">
                                <span className="input-group-text bg-light border-end-0 text-muted"><Calendar size={14} /></span>
                                <input type="date" className="form-control bg-light border-start-0" value={fromDate} onChange={e => setFromDate(e.target.value)} />
                            </div>
                        </div>

                        <div className="col-md-3">
                            <label className="small fw-bold text-muted mb-1">To Date</label>
                            <div className="input-group input-group-sm custom-input-group">
                                <span className="input-group-text bg-light border-end-0 text-muted"><Calendar size={14} /></span>
                                <input type="date" className="form-control bg-light border-start-0" value={toDate} onChange={e => setToDate(e.target.value)} />
                            </div>
                        </div>
                    </div>
                </div>

                {/* TABLE */}
                <div className="card border-0 shadow-sm overflow-hidden rounded-4">
                    <div className="table-responsive">
                        <table className="table table-hover align-middle mb-0">
                            <thead className="bg-navy text-white">
                                <tr className="small text-uppercase">
                                    <th className="ps-4 py-3 border-0">ID</th>
                                    <th className="border-0">Business Entity</th>
                                    <th className="border-0">Status</th>
                                    <th className="pe-4 border-0">Joined Date</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white">
                                {list?.length > 0 ? (
                                    list.map(user => (
                                        <tr key={user?.user_id} className="border-bottom">
                                            <td className="ps-4 fw-bold text-primary small">#{user?.user_id}</td>
                                            <td>
                                                <div className="d-flex align-items-center gap-3">
                                                    <div className="logo-container shadow-sm border">
                                                        <img
                                                            src={
                                                                user?.business_profile?.logo
                                                                    // ? 'http://localhost/business_community/' + user?.business_profile?.logo
                                                                    ? 'https://qnexus.in/community/' + user?.business_profile?.logo
                                                                    : `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='35' height='35' viewBox='0 0 35 35'%3E%3Crect width='35' height='35' rx='17.5' fill='%23e2e8f0'/%3E%3Ctext x='50%25' y='54%25' dominant-baseline='middle' text-anchor='middle' font-size='14' fill='%2394a3b8'%3E%3F%3C/text%3E%3C/svg%3E`
                                                            }
                                                            alt=""
                                                            width="35"
                                                            height="35"
                                                            className="rounded-circle"
                                                            onError={e => {
                                                                e.target.onerror = null;
                                                                e.target.src = `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='35' height='35' viewBox='0 0 35 35'%3E%3Crect width='35' height='35' rx='17.5' fill='%23e2e8f0'/%3E%3Ctext x='50%25' y='54%25' dominant-baseline='middle' text-anchor='middle' font-size='14' fill='%2394a3b8'%3E%3F%3C/text%3E%3C/svg%3E`;
                                                            }}
                                                        />
                                                    </div>
                                                    <span className="fw-bold text-navy">
                                                        {user?.business_profile?.business_name || "—"}
                                                    </span>
                                                </div>
                                            </td>
                                            <td>
                                                <span className={`badge px-3 py-2 rounded-pill ${user?.status === "ACTIVE"
                                                        ? "bg-soft-success text-success"
                                                        : "bg-soft-secondary text-secondary"
                                                    }`}>
                                                    {user?.status}
                                                </span>
                                            </td>
                                            <td className="pe-4 text-muted small">
                                                <div className="d-flex align-items-center">
                                                    <Calendar size={12} className="me-2 text-primary opacity-50" />
                                                    {user?.joined_at}
                                                </div>
                                            </td>
                                        </tr>
                                    )) 
                                ) : (
                                    <tr>
                                        <td colSpan="4" className="text-center text-muted py-5">
                                            <div className="opacity-50 mb-2">No businesses profiles match your search.</div>
                                            <button className="btn btn-sm btn-link text-primary" onClick={() => { setSearch(""); setFromDate(""); setToDate(""); }}>Clear Filters</button>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* PAGINATION */}
                {pagination?.total_pages > 1 && (
                    <div className="d-flex justify-content-between align-items-center mt-4 bg-white p-3 rounded-3 shadow-sm border-start border-primary border-4">
                        <span className="text-muted small">Page <b className="text-primary">{pagination?.page}</b> of {pagination?.total_pages}</span>
                        <div className="d-flex gap-2">
                            <button
                                className="btn btn-sm btn-outline-primary px-3 d-flex align-items-center shadow-none"
                                disabled={page <= 1}
                                onClick={() => setPage(p => p - 1)}
                            >
                                <ChevronLeft size={16} className="me-1" /> Prev
                            </button>
                            <button
                                className="btn btn-sm btn-primary px-3 d-flex align-items-center shadow-sm"
                                disabled={page >= pagination?.total_pages}
                                onClick={() => setPage(p => p + 1)}
                            >
                                Next <ChevronRight size={16} className="ms-1" />
                            </button>
                        </div>
                    </div>
                )}
            </div>

            <style>{`
                .bg-soft-blue { background-color: #f0f4f8; }
                .text-navy { color: #1a365d; }
                .bg-navy { background-color: #1e3a8a; }
                
                .icon-box-blue {
                    background: #1e3a8a;
                    color: white;
                    padding: 10px;
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }

                .custom-input-group .form-control:focus {
                    background-color: #fff;
                    border-color: #1e3a8a;
                    box-shadow: none;
                }

                .logo-container {
                    width: 38px;
                    height: 38px;
                    background: #fff;
                    border-radius: 50%;
                    padding: 2px;
                }

                .bg-soft-success { background-color: #e6fffa; border: 1px solid #b2f5ea; }
                .bg-soft-secondary { background-color: #f7fafc; border: 1px solid #edf2f7; }
                .text-success { color: #2c7a7b !important; }

                .table thead th { font-size: 0.75rem; letter-spacing: 0.05rem; border: none; }
                .table tbody tr:hover { background-color: #f8fafc !important; }
                
                .rounded-4 { border-radius: 1rem !important; }
            `}</style>
        </div>
    );
};

export default AdminBusinessList;